import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT from 'datatables.net';
import type {Instance, InstanceDataProps} from "../../../lib/ModelTypes.ts";
import {Button, Form, Modal} from "react-bootstrap";
import React, {
    type ChangeEvent,
    type  FormEvent,
    forwardRef,
    type  Ref,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import {PortForwardingDialog} from "../../../pages/instance/PortForwardingDialog.tsx";
import {swal_util} from "../../../lib/SwalUtil.ts";
import {snapshot, SnapshotAPIConnectorError} from "../../../lib/Snapshot.ts";

import {useNavigate} from "react-router-dom";
import {frontBaseUrl} from "../../../build_constants.ts";
import type {InstanceDataRef, InstanceRowEventHandlers, RowColumnRender} from "./type.ts";
import {createInstanceDatatableColumns, createInstanceDataTableOptions} from "./InstanceTableArgumentFactory.ts";
import type {ApiRowMethods} from "datatables.net-dt";
import {useTranslation} from "react-i18next";
import {getDatatableLanguagePack} from "../DatatableLanguagePack.tsx";

type DataTableProps = {
    instances?: Instance[]
    ref?: Ref<InstanceDataRef>;
    dataLoading: boolean;
    setPageLoading: (data: boolean) => void;
    setLoadingText: (data: string) => void;
    instanceRowEventHandlers: InstanceRowEventHandlers;
};

const InstanceTable = forwardRef<InstanceDataRef, DataTableProps>(
    (props, ref) => {
        const {t, i18n} = useTranslation("ui");
        const {instances = [], dataLoading, setPageLoading, setLoadingText, instanceRowEventHandlers} = props;
        const navigate = useNavigate();
        const [showCreateSnapshotModal, setShowCreateSnapshotModal] = useState(false);
        const [showPortForwardingModal, setShowPortForwardingModal] = useState(false);
        const [data, setData] = useState<InstanceDataProps>({
            selectedId: 0,
            selectedName: '',
            snapshot: '',
            proto: 'TCP',
            port: '',
            portTableData: [],
            checked: {},
        });
        const rowsMap = useRef(new Map<number, ApiRowMethods<Instance>>());

        function closeCreateSnapshotModal() {
            setShowCreateSnapshotModal(false);
        }

        function closePortForwardingModal() {
            setShowPortForwardingModal(false);
        }

        function updateSnapshot(e: ChangeEvent<HTMLInputElement>) {
            const snapshot = e.target.value;
            setData((prev: InstanceDataProps) => ({...prev, snapshot}));
        }

        function onClickSelectAllInstance(e: React.UIEvent) {
            const otherCheckboxes = document.getElementsByClassName("check-instance");
            Array.from(otherCheckboxes).forEach((element) => {
                const checkbox = element as HTMLInputElement;
                checkbox.checked = (e.target as HTMLInputElement).checked;
            });
            instances.forEach((instance) => {
                onChangeInstanceCheckBox(instance.id, (e.target as HTMLInputElement).checked)
            })
        }

        function onChangeInstanceCheckBox(id: number, checked: boolean) {
            setData((prevData) => ({
                ...prevData,
                checked: {
                    ...prevData.checked,
                    [id]: checked,
                },
            }));
        }

        async function onSubmitSnapshotDialogForm(e: FormEvent<HTMLFormElement>) {
            e.preventDefault();
            if (data.snapshot.length === 0) {
                swal_util.show_warning(t("snapshot.enterName")).finally();
                return;
            }

            try {
                await snapshot.create(data.selectedId, data.snapshot)
                const confirm = await swal_util.show_question(t("snapshot.createDone"), t("snapshot.goToPageQuestion"))
                if (confirm.isConfirmed) {
                    navigate(`${frontBaseUrl}/snapshot`);
                } else {
                    closeCreateSnapshotModal();
                }
                setData((prev: InstanceDataProps) => ({...prev, snapshot: ''}))
            } catch (err) {
                if (err instanceof SnapshotAPIConnectorError) {
                    swal_util.show_error(`${err.localization}`).finally();
                } else {
                    swal_util.show_error(t("snapshot.createError")).finally();
                }
                console.log(err);
            }

            return false;
        }

        const instanceColumns = createInstanceDatatableColumns(t);
        const columnRendersMap = instanceColumns.reduce(
            (acc: Record<string, RowColumnRender[]>, {data, render}, index) => {
                const element = acc[data];
                if (!element) {
                    acc[data] = [];
                }

                acc[data].push({
                    index: index,
                    render: render as (data: unknown) => number | string,
                });

                return acc;
            },
            {}
        );
        const baseOptions = createInstanceDataTableOptions(
            data,
            setData,
            instanceRowEventHandlers,
            setPageLoading,
            setLoadingText,
            setShowCreateSnapshotModal,
            setShowPortForwardingModal,
            onChangeInstanceCheckBox,
        );

        const datatableOptions = {
            ...baseOptions,
            language: getDatatableLanguagePack(i18n.resolvedLanguage),
            destroy: true,
        };
        DataTable.use(DT);

        const dataTableRef = useRef<DataTableRef>(null);

        useImperativeHandle(ref, () => ({
            getSelectedData() {
                return instances.filter((instance) => data.checked[instance.id]);
            },
            invalidate() {
                const table = dataTableRef.current?.dt();
                let invalidated = false;

                if (!table) return;
                if (rowsMap.current.size == 0) {
                    table.clear();
                }

                instances.forEach(instance => {
                    const apiRow = rowsMap.current.get(instance.id);

                    if (!apiRow) { // 새롭게 추가된 인스턴스
                        rowsMap.current.set(instance.id, table.row.add(instance));
                        invalidated = true;
                    } else { // 이미 그려진 인스턴스
                        const rowElement = apiRow.node();
                        const cells = rowElement.querySelectorAll('td, th');
                        const instanceUnsafe = instance as unknown as Record<string, unknown>;
                        const tableDrawnInstanceUnsafe = apiRow.data() as unknown as Record<string, unknown>;

                        // ui interaction이 없는 상태 필드만 아래 방식으로 변경.
                        ["power_state", "created", "status", "OS-EXT-STS:task_state", "OS-EXT-STS:power_state"].forEach(
                            (key) => {
                                if (tableDrawnInstanceUnsafe[key] == instanceUnsafe[key]) return;

                                tableDrawnInstanceUnsafe[key] = instanceUnsafe[key];

                                const columnRenders = columnRendersMap[key];
                                if (!columnRenders) return;
                                for (const columnRender of columnRenders) {
                                    const render = columnRender.render;
                                    const columnIndex = columnRender.index;
                                    if (!render) {
                                        cells[columnIndex].innerHTML = (instanceUnsafe[key] as object).toString();
                                    } else {
                                        const meta = {
                                            settings: apiRow.context[0],
                                            row: apiRow.index(),
                                            col: columnIndex
                                        };
                                        cells[columnIndex].innerHTML = render(instanceUnsafe[key], "display", instance, meta).toString();
                                    }
                                }
                            }
                        );
                    }
                });

                const keys = [...rowsMap.current.keys()];
                for (const key of keys) {
                    if (!instances.some(instance => instance.id === key)) {
                        rowsMap.current.get(key)?.remove();
                        rowsMap.current.delete(key);
                        invalidated = true;
                    }
                }

                if (invalidated) {
                    table.draw();
                }
            }
        }));


        return (
            <div style={{fontSize: "14px"}}>
                <DataTable
                    key={`dt-${i18n.resolvedLanguage}`}
                    ref={dataTableRef}
                    data={instances}
                    columns={instanceColumns}
                    className="display"
                    options={datatableOptions}
                >
                    <thead>
                    <tr>
                        <th className="text-center">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                onClick={(e) => onClickSelectAllInstance(e)}
                            />
                        </th>
                        <th>{t("instance.instanceName")}</th>
                        <th>{t("instance.keypair")}</th>
                        <th>{t("instance.os")}</th>
                        <th>{t("instance.ipAddress")}</th>
                        <th>{t("instance.flavor")}</th>
                        <th>{t("instance.status")}</th>
                        <th>{t("instance.currentTask")}</th>
                        <th>{t("instance.powerState")}</th>
                        <th>{t("instance.elapsedSinceCreation")}</th>
                        <th>{t("common.action")}</th>
                    </tr>
                    </thead>

                    <tfoot>
                    <tr>
                        <td colSpan={instanceColumns.length} className="text-start text-secondary border-bottom">
                            <span>{dataLoading ? t("instance.loadingList") : t("instance.instanceCount", {count: instances.length})}</span>
                        </td>
                    </tr>
                    </tfoot>
                </DataTable>
                <Modal show={showCreateSnapshotModal} onHide={closeCreateSnapshotModal}>
                    <Modal.Header>
                        <Modal.Title><h6 className="text-primary pt-2">{t("snapshot.createTitle")}</h6></Modal.Title>
                        <Button variant="white" className="btn_close" onClick={closeCreateSnapshotModal}>X</Button>
                    </Modal.Header>
                    <Modal.Body className="pt-1 px-1">
                        <Form className="modal-body" onSubmit={onSubmitSnapshotDialogForm}>
                            <div className="row"></div>
                            <div className="row">
                                <p className="col-md-4 pt-2">{t("snapshot.name")}</p>
                                <div className="col-md-8">
                                    <input
                                        type="text"
                                        className="form-control text-secondary"
                                        onChange={updateSnapshot}/>
                                </div>

                            </div>
                            <div className="row pt-4">
                                <div className="d-grid gap-2">
                                    <Button variant="primary" type="submit"
                                            className="rounded-3  btn">{t("common.add")}</Button>
                                </div>

                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>
                <PortForwardingDialog
                    close={closePortForwardingModal}
                    show={showPortForwardingModal}
                    data={data}
                    setData={setData}/>
            </div>
        );
    });

export default InstanceTable;